import React, { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation, useNavigationType } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { MapPin, Phone, Menu, X, ChevronDown, MessageCircle } from "lucide-react";
import { CONTACT, NAV, MENU_SERVICES, aurea, logo } from "../data/site";
import { openAiChat } from "../aichat";
import { SocialRow } from "./shared";
import { useDialogA11y, useFocusReturn } from "../a11y";
import { QuoteLink } from "./QuotePanel";
import { imgProps } from "./media";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const loc = useLocation();
  const navType = useNavigationType();
  const reduce = useReducedMotion();
  const savedY = useRef(0);
  const navClose = useRef(false);
  const menuRef = useRef(null);
  const closeMenu = () => setOpen(false);
  useDialogA11y(menuRef, open, closeMenu);
  useFocusReturn(open);
  useEffect(() => {
    navClose.current = true;
    setOpen(false);
    setSvcOpen(false);
  }, [loc.pathname]);
  useEffect(() => {
    document.body.classList.toggle("mmenu-open", open);
    document.body.style.overflow = "";
    if (open) {
      savedY.current = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = "fixed";
      document.body.style.top = `-${savedY.current}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    } else {
      const was = document.body.style.position === "fixed";
      document.documentElement.style.scrollBehavior = "auto";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      if (was && navClose.current && navType === "POP") {
        window.scrollTo(0, savedY.current);
      } else if (was && navClose.current) {
        window.scrollTo(0, 0);
      } else if (was) {
        window.scrollTo(0, savedY.current);
      }
      document.documentElement.style.scrollBehavior = "";
      navClose.current = false;
    }
  }, [open]);

  return (
    <>
      <div className="topbar">
        <div className="topbar-in">
          <span style={{ display: "inline-flex", gap: 10, alignItems: "center" }}>
            <MapPin size={16} className="ic" /> {CONTACT.address}
          </span>
          <a href={CONTACT.phoneHref} data-testid="topbar-phone">
            <Phone size={16} className="ic" /> Chiama: risponde la nostra assistente virtuale, 24 ore su 24
          </a>
          <span className="right">
            <span className="livedot" aria-hidden="true" />
            Centrali operative attive 24/7
          </span>
        </div>
      </div>

      <div className="hdr-holder">
        <header className="hdr">
          <div className="hdr-in">
            <div className="hdr-left">
              <Link to="/" className="hdr-logo" aria-label="Orion Security, home" data-testid="header-logo-link">
                <img {...imgProps(logo("orion-bianco.png"), { sizes: "160px", lazy: false })} alt="Orion Security" />
              </Link>
              <a href="/chi-siamo#gruppo" className="byaurea" aria-label="by Gruppo Aurea: Orion Security fa parte del Gruppo Aurea Holding 3" data-testid="header-by-aurea">
                <img {...imgProps(aurea("emblema.png"), { sizes: "34px", lazy: false })} alt="" />
                <span>
                  by{" "}
                  <strong>Gruppo Aurea</strong>
                </span>
              </a>
            </div>
            <nav className="nav" aria-label="Menu principale">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} className={({ isActive }) => `nl ${isActive ? "on" : ""}`} data-testid={`nav-${n.to === "/" ? "home" : n.to.replace("/", "")}`}>
                  {n.label}
                </NavLink>
              ))}
              <button type="button" className="menubtn" aria-label="Apri il menu" onClick={() => setOpen(true)} data-testid="menu-open-button">
                <Menu size={18} /> Menu
              </button>
              <QuoteLink className="cta" testid="header-cta">
                Preventivo gratuito
              </QuoteLink>
            </nav>
          </div>
        </header>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={menuRef}
            className="mmenu"
            initial={{ opacity: reduce ? 1 : 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: reduce ? 1 : 0 }}
            transition={{ duration: reduce ? 0 : 0.2 }}
            data-testid="mobile-menu"
          >
            <div className="mmenu-top">
              <Link to="/" aria-label="Orion Security, home" data-testid="mobile-menu-logo" onClick={() => setOpen(false)}>
                <img {...imgProps(logo("orion-bianco.png"), { sizes: "160px" })} alt="Orion Security" />
              </Link>
              <button type="button" className="mclose" aria-label="Chiudi il menu" onClick={() => setOpen(false)} data-testid="menu-close-button">
                <X size={24} />
              </button>
            </div>

            <div className="mmenu-links">
              {NAV.map((n, i) =>
                n.label === "Servizi" ? (
                  <motion.div key={n.to} initial={{ opacity: reduce ? 1 : 0, y: reduce ? 0 : 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduce ? 0 : 0.3, delay: reduce ? 0 : 0.2 + i * 0.04 }}>
                    <div className="mm-row">
                      <NavLink to={n.to} onClick={closeMenu} className={({ isActive }) => `mm-item ${isActive ? "on" : ""}`} data-testid="mobile-nav-servizi">
                        {n.label}
                      </NavLink>
                      <button
                        type="button"
                        className={`mm-arrow ${svcOpen ? "open" : ""}`}
                        aria-label={svcOpen ? "Chiudi l'elenco dei servizi" : "Apri l'elenco dei servizi"}
                        aria-expanded={svcOpen}
                        onClick={() => setSvcOpen((v) => !v)}
                        data-testid="mobile-servizi-arrow"
                      >
                        <ChevronDown size={22} />
                      </button>
                    </div>
                    <AnimatePresence initial={false}>
                      {svcOpen && (
                        <motion.div
                          className="msvc"
                          initial={{ height: reduce ? "auto" : 0, opacity: reduce ? 1 : 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: reduce ? "auto" : 0, opacity: reduce ? 1 : 0 }}
                          transition={{ duration: reduce ? 0 : 0.25, ease: [0.2, 0.7, 0.2, 1] }}
                          data-testid="mobile-servizi-sub"
                        >
                          {MENU_SERVICES.map(([label, to]) => (
                            <NavLink key={label + to} to={to} onClick={closeMenu} className={({ isActive }) => `msvc-it ${isActive ? "on" : ""}`} data-testid={`mobile-svc-${to.replace("/servizi/", "")}`}>
                              {label}
                            </NavLink>
                          ))}
                          <NavLink to="/servizi" onClick={closeMenu} className="msvc-it all" data-testid="mobile-svc-tutti">
                            Tutti i servizi
                          </NavLink>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ) : (
                  <motion.div key={n.to} initial={{ opacity: reduce ? 1 : 0, y: reduce ? 0 : 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduce ? 0 : 0.3, delay: reduce ? 0 : 0.2 + i * 0.04 }}>
                    <NavLink to={n.to} end={n.to === "/"} onClick={closeMenu} className={({ isActive }) => `mm-item ${isActive ? "on" : ""}`} data-testid={`mobile-nav-${n.to === "/" ? "home" : n.to.replace("/", "")}`}>
                      {n.label}
                    </NavLink>
                  </motion.div>
                )
              )}
            </div>

            <div className="mm-bottom">
              <QuoteLink className="btn mm-cta" testid="mobile-menu-cta" onClick={closeMenu} openDelay={240}>
                Preventivo gratuito
              </QuoteLink>
              <div className="mm-pills">
                <a href="tel:+3906232514" className="mm-pill" data-testid="mobile-menu-call">
                  <Phone size={18} /> Chiama
                </a>
                <a href="#assistente-ai" className="mm-pill" onClick={(e) => { e.preventDefault(); closeMenu(); setTimeout(openAiChat, 240); }} data-testid="mobile-menu-chat">
                  <MessageCircle size={18} color="#1EA7E8" /> Chat AI
                </a>
              </div>
              <SocialRow testid="social-menu" centered />
              <div className="mm-aurea" data-testid="mobile-menu-aurea">
                <span className="goldline" aria-hidden="true" />
                <img {...imgProps(aurea("emblema.png"), { sizes: "34px" })} alt="" />
                <span>by Gruppo Aurea</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
